export const fabricationExecutionMode = {
    Ordered: 'ORDERED',
    AnyOrder: 'ANY_ORDER',
  } as const;
  
  export const fabricationReleaseRole = {
    FrameComponent: 'FRAME_COMPONENT',
    Panel: 'PANEL',
    Other: 'OTHER',
  } as const;
  
  export const fabricationWorkOrderStatus = {
    Draft: 'DRAFT',
    Pending: 'PENDING',
    InProgress: 'IN_PROGRESS',
    Completed: 'COMPLETED',
    OnHold: 'ON_HOLD',
    Cancelled: 'CANCELLED',
  } as const;
  
  export namespace FabricationType {
    export type ExecutionMode =
      (typeof fabricationExecutionMode)[keyof typeof fabricationExecutionMode];
  
    export type ReleaseRole =
      (typeof fabricationReleaseRole)[keyof typeof fabricationReleaseRole];
  
    export type WorkOrderStatus =
      (typeof fabricationWorkOrderStatus)[keyof typeof fabricationWorkOrderStatus];
  
    export type Machine = {
      id: number;
      created_at: string;
      updated_at: string;
      project: number;
      name: string;
      code: string | null;
      description: string | null;
      is_active: boolean;
      metadata: Record<string, any>;
    };
  
    export type MachineUpsert = {
      name: string;
      code?: string | null;
      description?: string | null;
      is_active?: boolean;
      metadata?: Record<string, any>;
    };
  
    export type ProcessStepMachine = {
      id: number;
      name: string;
      code: string | null;
    };
  
    export type ProcessStep = {
      id: number;
      created_at: string;
      updated_at: string;
      process: number;
      name: string;
      code: string | null;
      sequence: number;
      description: string | null;
      estimated_minutes: number | null;
      allowed_machines: ProcessStepMachine[];
    };
  
    export type ProcessStepUpsert = {
      name: string;
      code?: string | null;
      sequence: number;
      description?: string | null;
      estimated_minutes?: number | null;
      allowed_machine_ids?: number[];
    };
  
    export type Process = {
      id: number;
      created_at: string;
      updated_at: string;
      project: number;
      name: string;
      code: string | null;
      execution_mode: ExecutionMode;
      description: string | null;
      is_active: boolean;
      steps: ProcessStep[];
    };
  
    export type ProcessUpsert = {
      name: string;
      code?: string | null;
      execution_mode?: ExecutionMode;
      description?: string | null;
      is_active?: boolean;
    };
  
    export type SummaryRow = {
      release: number;
      part_id: number;
      part_number: string;
      project_name?: string | null;
      release_number?: string | null;
      data: Record<string, any>;
      required_qty: number;
      role: ReleaseRole;
      inventory_only: boolean;
      fabrication_required: boolean;
      is_active: boolean;
      notes: string | null;
      fabricated_qty: number;
      manual_fabricated_qty?: number;
      open_work_order_qty: number;
      remaining_qty: number;
      frame_names: string[];
      frame_count: number;
      process_names: string[];
      work_order_count: number;
      progress_steps_preview: PartProgressStepItem[];
    };
  
    export type WorkOrderCandidate = {
      release: number;
      part_id: number;
      part_number: string;
      project_name?: string | null;
      release_number?: string | null;
      data: Record<string, any>;
      required_qty: number;
      open_work_order_qty: number;
      fabricated_qty: number;
      manual_fabricated_qty?: number;
      remaining_qty: number;
      role: ReleaseRole;
      inventory_only: boolean;
      fabrication_required: boolean;
      is_active: boolean;
      notes: string | null;
      frame_names: string[];
      frame_count: number;
    };
  
    export type SummaryConfigPatch = {
      role?: ReleaseRole;
      inventory_only?: boolean;
      fabrication_required?: boolean;
      is_active?: boolean;
      notes?: string | null;
    };

    export type ReleaseSettings = {
      id: number;
      created_at: string;
      updated_at: string;
      release: number;
      default_work_order_due_date: string | null;
    };

    export type ReleaseSettingsPatch = {
      default_work_order_due_date?: string | null;
    };

    export type ManualCompletionItem = {
      part_id: number;
      quantity: number;
      notes?: string | null;
    };

    export type ManualCompletionResponse = {
      completed_parts: {
        part_id: number;
        part_number: string;
        quantity: number;
        manual_completion_id: number;
        fabricated_qty: number;
        remaining_qty: number;
        work_order_count: number;
        execution_log_ids: number[];
      }[];
      manual_completion_count: number;
      execution_log_count: number;
    };
  
    export type FabricationConfig = {
      id: number;
      created_at: string;
      updated_at: string;
      release: number;
      part: number;
      role: ReleaseRole;
      inventory_only: boolean;
      fabrication_required: boolean;
      is_active: boolean;
      notes: string | null;
    };
  
    export type AssignedUser = {
      id: number;
      username: string;
      display_name: string;
    };
  
    export type WorkOrderStep = {
      id: number;
      created_at: string;
      updated_at: string;
      process_step: number | null;
      machine: number | null;
      machine_name: string | null;
      step_name_snapshot: string;
      step_code_snapshot: string | null;
      sequence: number;
      qty_planned: number;
      completed_qty_cache: number;
      status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
      assigned_users: AssignedUser[];
    };
  
    export type WorkOrder = {
      id: number;
      created_at: string;
      updated_at: string;
      project: number;
      release: number;
      part: number;
      part_number: string;
      process: number;
      process_name: string;
      preferred_machine: number | null;
      preferred_machine_name: string | null;
      identifier: string | null;
      description: string | null;
      status: WorkOrderStatus;
      qty_planned: number;
      qty_completed_cache: number;
      due_date: string | null;
      created_by: number | null;
      assigned_users: AssignedUser[];
      steps: WorkOrderStep[];
    };
  
    export type AssignedWorkOrder = WorkOrder;
  
    export type WorkOrderCreateItem = {
      part_id: number;
      process_id: number;
      qty_planned: number;
      description?: string | null;
      due_date?: string | null;
      step_user_assignments: {
        process_step_id: number;
        user_ids: number[];
      }[];
    };
  
    export type WorkOrderBulkPatch = {
      work_order_ids: number[];
      assigned_user_ids?: number[];
      preferred_machine_id?: number | null;
      due_date?: string | null;
      status?: WorkOrderStatus;
      description?: string | null;
    };

    export type PartProgressTimelineItem = {
      id: number;
      timestamp: string | null;
      user: string;
      work_order_id: number | null;
      work_order_identifier: string | null;
      step_name: string | null;
      step_sequence: number | null;
      machine_name: string | null;
      quantity: number;
      inventory_delta: number;
      note: string | null;
      source?: string | null;
    };

    export type PartProgressWorkOrder = {
      id: number;
      identifier: string | null;
      process_name: string | null;
      status: string;
      qty_planned: number;
      qty_completed: number;
      due_date: string | null;
    };

    export type PartProgressStepItem = {
      step_name: string;
      sequence: number;
      qty_planned: number;
      qty_completed: number;
      completion_pct: number;
    };

    export type PartProgressUserContribution = {
      user: string;
      quantity_completed: number;
    };

    // Aliases kept for backward compatibility with existing components.
    export type SummaryStepProgress = PartProgressStepItem;
    export type ProgressTimelineItem = PartProgressTimelineItem;

    export type PartProgress = {
      part_id: number;
      part_number: string;
      project_name: string | null;
      release_number: string | null;
      required_qty: number;
      fabricated_qty: number;
      manual_fabricated_qty?: number;
      open_work_order_qty: number;
      remaining_qty: number;
      frame_names: string[];
      frame_count: number;
      process_names: string[];
      work_order_count: number;
      progress_steps: PartProgressStepItem[];
      user_contribution: PartProgressUserContribution[];
      timeline: PartProgressTimelineItem[];
      work_orders: PartProgressWorkOrder[];
    };
  
    export type ExecutionInput = {
      step_id: number;
      quantity: number;
      machine_id?: number | null;
      note?: string | null;
      push_to_inventory?: boolean;
    };
  
    export type ExecutionLog = {
      id: number;
      created_at: string;
      updated_at: string;
      work_order: number;
      step: number;
      step_name: string;
      machine: number | null;
      machine_name: string | null;
      logged_by: number | null;
      logged_by_name: string | null;
      quantity: number;
      inventory_delta: number;
      note: string | null;
    };
  
    export type InventoryMovement = {
      id: number;
      created_at: string;
      updated_at: string;
      execution_log: number;
      project: number;
      release: number;
      part: number;
      part_number: string;
      work_order: number;
      movement_type: string;
      quantity_delta: number;
      note: string | null;
      created_by: number | null;
    };

    export type InventorySummaryRow = {
      part_id: number;
      part_number: string;
      available_qty: number;
      on_hold_qty: number;
      fabricated_output_qty: number;
      movement_count: number;
      work_order_count: number;
      work_order_identifiers?: string[];
      release_count: number;
      release_numbers?: string[];
      last_output_at: string | null;
    };
  }
  export type InventorySummaryRow = {
    part_id: number;
    part_number: string;
    available_qty: number;
    on_hold_qty: number;
    fabricated_output_qty: number;
    movement_count: number;
    work_order_count: number;
    work_order_identifiers?: string[];
    release_count: number;
    release_numbers?: string[];
    last_output_at: string | null;
  };